import { Alert, CircularProgress } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { jwtDecode } from "jwt-decode";
import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { Helmet } from "react-helmet";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import portnetLogo from "../../../assets/images/portnetLogo.png";
import config from "../../../common/Config";
import {
  CONSULTATION_TITRES_PAGE,
  GESTION_TITRES_PAGE,
} from "../../../common/constants/routerConstants";
import { validateAuthentification } from "../../../common/utils/validateUtils";
import {
  authenticate,
  registerMFA,
  sendOtpMail,
} from "../../TI/state/TiActions";
import MFAPopup from "./MFAPopup";
import "./Auth.css";
import axios from "axios";

const FEATURE_BADGES = [
  { icon: ShieldOutlinedIcon, label: "Connexion sécurisée" },
  { icon: AccessTimeIcon, label: "Disponibilité 24/24" },
  { icon: SpeedOutlinedIcon, label: "Performance et traçabilité" },
];

const FOOTER_LINKS = [
  // { label: "Mentions légales", href: "https://www.portnet.ma/mentions-legales" },
  // { label: "Confidentialité", href: "https://www.portnet.ma/confidentialite" },
  // { label: "CGU", href: "https://www.portnet.ma/cgu" },
  // { label: "Accessibilité", href: "https://www.portnet.ma/accessibilite" },
];

const Auth = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showMfaPopup, setShowMfaPopup] = useState(false);
  const [showOtpVerification, setShowOtpVerification] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [codeOTP, setCodeOTP] = useState(null);
  const [username, setUsername] = useState(localStorage.getItem("user") || "");
  const [password, setPassword] = useState(localStorage.getItem("pass") || "");
  const [rememberMe, setRememberMe] = useState(
    !!localStorage.getItem("rememberMe"),
  );
  const JToken = useSelector((state) => state.authentificationReducer.token);
  const authError = useSelector((state) => state.authentificationReducer.error);
  const mfaError = useSelector(
    (state) => state.authentificationReducer.mfaErrorMessage,
  );
  const otpMailError = useSelector(
    (state) => state.authentificationReducer.otpMailError,
  );
  const { qrCodeUrl, secret } = useSelector(
    (state) => state.authentificationReducer,
  );
  const isLoading = useSelector(
    (state) => state.authentificationReducer.isLoading,
  );
  const authResponse = useSelector((state) => state.authentificationReducer);
  const [errorMessage, setErrorMessage] = useState("");
  const [mfaErrorMessage, setMfaErrorMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [otpMessage, setOtpMessage] = useState("");
  const titreReducer = useSelector((state) => state.titreReducer);
  const captchaRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();
  const [isChecked, setIsChecked] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const [isModeConsultation] = useState(titreReducer?.consultingMode);

  useEffect(() => {
    if (JToken !== null) {
      const decoded = jwtDecode(JToken);
      if (
        decoded.roles?.includes("ROLE_VUPORTUARIA_BANCO") ||
        decoded.roles?.includes("ROLE_VUPORTUARIA_ADMIN") ||
        decoded.roles?.includes("ROLE_VUPORTUARIA_ANP")
      ) {
        localStorage.setItem("token", JToken);
        if (rememberMe) {
          localStorage.setItem("user", username);
          localStorage.setItem("pass", password);
          localStorage.setItem("rememberMe", true);
        } else {
          localStorage.removeItem("user");
          localStorage.removeItem("pass");
          localStorage.removeItem("rememberMe");
        }
        if (isModeConsultation) {
          navigate(CONSULTATION_TITRES_PAGE);
        } else {
          navigate(GESTION_TITRES_PAGE);
        }
      } else {
        setShowOtpVerification(false);
        setShowMfaPopup(false);
        setErrorMessage("Nom d'utilisateur et/ou mot de passe incorrecte");
      }
    }
  }, [JToken]);

  useEffect(() => {
    if (authResponse) {
      if (authResponse.qrCodeUrl && authResponse.secret && authResponse.mail) {
        setShowMfaPopup(true);
      } else if (authResponse.requireOTP === "true") {
        setShowOtpVerification(true);
        setOtpMessage(
          authResponse.message ||
            "Veuillez saisir le code à 6 chiffres affiché sur votre application mobile.",
        );
      }
    }
  }, [authResponse]);

  useEffect(() => {
    if (authResponse && authResponse.message && showOtpVerification) {
      setOtpMessage(authResponse.message);
    }
  }, [authResponse.message, showOtpVerification]);

  const handleRegisterClick = () => {
    setMfaErrorMessage("");
    const payload = {
      enableMailMfa: true,
      username,
      password,
      otp: codeOTP,
      secret,
    };

    dispatch(registerMFA(payload));
    setCodeOTP(null);
  };

  const handleLoginClick = () => {
    setErrorMessage("");

    if (showOtpVerification) {
      if (!codeOTP || codeOTP.length < 6) {
        setErrorMessage("Code OTP incorrect. Veuillez réessayer.");
        return;
      }

      dispatch(
        authenticate({
          username,
          password,
          otp: codeOTP,
        }),
      );
      setCodeOTP(null);
      return;
    }

    const { isValid, errors: validationErrors } = validateAuthentification({
      username,
      password,
    });
    setErrors(validationErrors);
    if (isValid) {
      if (!isChecked) {
        showErrorSnackbar("Veuillez cocher la case 'Je ne suis pas un robot'.");
        return;
      }

      dispatch(
        authenticate({
          username,
          password,
          otp: codeOTP,
          captchaToken,
        }),
      );

      captchaRef.current?.reset();
      setIsChecked(false);
      setCodeOTP(null);
    }
  };

  const onCaptchaChange = (value) => {
    setCaptchaToken(value);
    setIsChecked(!!value);
  };

  const showErrorSnackbar = (message) => {
    enqueueSnackbar(message, {
      variant: "error",
    });
  };

  const handleSendCodeByMail = () => {
    setErrorMessage("");
    dispatch(
      sendOtpMail({
        username,
        password,
        otp: null,
      }),
    );
  };

  const handleEnterKeyPress = (e) => {
    if (e.key === "Enter") {
      handleLoginClick();
    }
  };

  useEffect(() => {
    if (authError !== null && authError !== "") {
      const errorString = authError.toString();
      if (errorString?.includes("401") || errorString?.includes("500")) {
        if (showOtpVerification) {
          setErrorMessage("Code OTP incorrect. Veuillez réessayer.");
          setCodeOTP(null);
        } else {
          setErrorMessage("Nom d'utilisateur et/ou mot de passe incorrecte");
          setShowOtpVerification(false);
          setCodeOTP(null);
        }
      } else {
        setErrorMessage(
          "Erreur d'authentification, veuillez réessayer ultérieurement.",
        );
        if (!showOtpVerification) {
          setShowOtpVerification(false);
          setCodeOTP(null);
        }
      }
    }
  }, [authError, showOtpVerification]);

  useEffect(() => {
    if (mfaError !== null && mfaError !== "") {
      const errorString = mfaError.toString();
      if (errorString?.includes("401") || errorString?.includes("500")) {
        setMfaErrorMessage("Code OTP incorrect. Veuillez réessayer.");
      } else {
        setMfaErrorMessage("Erreur d'enregistrement MFA. Veuillez réessayer.");
      }
      setCodeOTP(null);
    }
  }, [mfaError]);

  useEffect(() => {
    if (otpMailError !== null && otpMailError !== "") {
      setErrorMessage(
        "Erreur lors de l'envoi du code OTP. Veuillez réessayer.",
      );
    }
  }, [otpMailError]);

  const handleForgotPassword = () => {
    setErrorMessage("");
    setErrors({});
    setUsername("");
    setShowForgotPassword(true);
  };

  const handleBackToLogin = () => {
    setErrorMessage("");
    setUsername("");
    setShowForgotPassword(false);
  };

  const handleResetRequest = async () => {
    setErrorMessage("");

    const login = username.trim();
    if (!login) {
      setErrorMessage("Veuillez saisir votre identifiant.");
      return;
    }

    try {
      const response = await axios.post(
        `${config.crcUrl}/api/user/resetPassword`,
        { username: login, portalName: "Portail Banques" },
      );

      enqueueSnackbar(
        response?.data?.message ||
          "Si un compte existe, un lien de réinitialisation a été envoyé.",
        { variant: "success" },
      );
      setUsername("");
      setShowForgotPassword(false);
    } catch (error) {
      enqueueSnackbar(
        "Une erreur s'est produite. Veuillez réessayer ultérieurement.",
        { variant: "error" },
      );
    }
  };

  return (
    <div className="auth-page">
      <Helmet>
        <title>Connexion - Portail Banques</title>
      </Helmet>

      {isLoading && (
        <div className="auth-loading-overlay">
          <CircularProgress sx={{ color: "#ffffff" }} />
        </div>
      )}
      <div className="auth-container">
        <aside className="auth-left-panel">
          <div className="auth-pre-align">
            <div className="auth-logo-container">
              <img src={portnetLogo} alt="PORTNET" className="auth-logo" />
            </div>
          </div>
          <div className="auth-align-block">
            <h1 className="auth-headline auth-headline--portail">
              Portail{" "}
              <span className="auth-headline-underline">
                <b>Banques</b>
              </span>
            </h1>

            <div className="auth-badges">
              {FEATURE_BADGES.map(({ icon: Icon, label }) => (
                <span key={label} className="auth-badge">
                  <Icon fontSize="small" />
                  {label}
                </span>
              ))}
            </div>
          </div>
        </aside>

        <div className="auth-right-column">
          <main className="auth-right-panel">
            <div className="auth-form-wrapper">
              <div className="auth-pre-align">
                {showForgotPassword ? (
                  <>
                    <div className="auth-section-label auth-section-label--red">
                      Mot de passe oublié
                    </div>
                    <h2 className="auth-form-title">
                      Réinitialiser mon mot de passe
                    </h2>
                    <p className="auth-form-subtitle">
                      Saisissez votre identifiant pour recevoir un lien de
                      réinitialisation.
                    </p>
                  </>
                ) : (
                  <>
                    <div className="auth-section-label auth-section-label--red">
                      Connexion
                    </div>
                    <h2 className="auth-form-title">Accéder à mon espace</h2>
                    <p className="auth-form-subtitle">
                      Renseignez vos identifiants opérateur pour piloter vos
                      opérations de commerce extérieur.
                    </p>
                  </>
                )}
                {errorMessage && (
                  <Alert className="auth-alert" severity="error">
                    {errorMessage}
                  </Alert>
                )}
              </div>

              <div className="auth-align-block">
                {showForgotPassword ? (
                  <>
                    <br />
                    <div className="auth-field-group">
                      <label
                        className="auth-field-label"
                        htmlFor="auth-reset-username"
                      >
                        Identifiant
                      </label>
                      <input
                        id="auth-reset-username"
                        className="auth-field-input"
                        placeholder="nom d'utilisateur"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleResetRequest();
                        }}
                        autoComplete="username"
                      />
                    </div>

                    <button
                      type="button"
                      className="auth-submit-btn"
                      style={{ marginTop: "1.5em" }}
                      onClick={handleResetRequest}
                    >
                      Soumettre
                      <ArrowForwardIcon fontSize="small" />
                    </button>

                    <button
                      type="button"
                      className="auth-back-btn"
                      onClick={handleBackToLogin}
                    >
                      <ArrowBackIcon fontSize="small" />
                      Retour à la connexion
                    </button>
                  </>
                ) : (
                  <>
                    {showOtpVerification ? (
                      <>
                        <p className="auth-otp-message">
                          {otpMessage ||
                            "Veuillez saisir le code à 6 chiffres affiché sur votre application mobile."}
                        </p>
                        <div className="auth-field-group">
                          <label
                            className="auth-field-label"
                            htmlFor="auth-otp"
                          >
                            Code OTP
                          </label>
                          <input
                            id="auth-otp"
                            className={`auth-field-input${
                              errors.codeOTP ? " auth-field-input--error" : ""
                            }`}
                            placeholder="••••••"
                            value={codeOTP || ""}
                            maxLength={6}
                            onChange={(e) => setCodeOTP(e.target.value)}
                            onKeyDown={handleEnterKeyPress}
                          />
                          {errors.codeOTP && (
                            <span className="auth-field-error">
                              {errors.codeOTP}
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          className="auth-otp-mail-link"
                          onClick={handleSendCodeByMail}
                        >
                          Envoyer le code par mail
                        </button>
                      </>
                    ) : (
                      <>
                        <br />
                        <div className="auth-field-group">
                          <label
                            className="auth-field-label"
                            htmlFor="auth-username"
                          >
                            Identifiant
                          </label>
                          <input
                            id="auth-username"
                            className={`auth-field-input${
                              errors.username ? " auth-field-input--error" : ""
                            }`}
                            placeholder="nom d'utilisateur"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            onKeyDown={handleEnterKeyPress}
                            autoComplete="username"
                          />
                          {errors.username && (
                            <span className="auth-field-error">
                              {errors.username}
                            </span>
                          )}
                        </div>

                        <div className="auth-field-group">
                          <label
                            className="auth-field-label"
                            htmlFor="auth-password"
                          >
                            Mot de passe
                          </label>
                          <div className="auth-password-wrapper">
                            <input
                              id="auth-password"
                              className={`auth-field-input auth-field-input--password${
                                errors.password
                                  ? " auth-field-input--error"
                                  : ""
                              }`}
                              type={showPassword ? "text" : "password"}
                              placeholder="••••••••"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              onKeyDown={handleEnterKeyPress}
                              autoComplete="current-password"
                            />
                            <button
                              type="button"
                              className="auth-password-toggle"
                              onClick={() => setShowPassword((prev) => !prev)}
                              aria-label="Afficher ou masquer le mot de passe"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </button>
                          </div>
                          {errors.password && (
                            <span className="auth-field-error">
                              {errors.password}
                            </span>
                          )}
                        </div>
                      </>
                    )}

                    <div
                      className="auth-form-options"
                      style={{ marginTop: "1.5em" }}
                    >
                      <label className="auth-remember-me">
                        <input
                          type="checkbox"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                        />
                        Se souvenir de moi
                      </label>

                      <button
                        type="button"
                        className="auth-forgot-link"
                        onClick={handleForgotPassword}
                      >
                        Mot de passe oublié ?
                      </button>
                    </div>

                    {!showOtpVerification && (
                      <div
                        className="auth-captcha"
                        style={{ marginTop: "1.25em" }}
                      >
                        <ReCAPTCHA
                          ref={captchaRef}
                          sitekey={config.captchaSiteKey}
                          onChange={onCaptchaChange}
                        />
                      </div>
                    )}

                    <button
                      type="button"
                      className="auth-submit-btn"
                      onClick={handleLoginClick}
                    >
                      Se connecter
                      <ArrowForwardIcon fontSize="small" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </main>

          <footer className="auth-footer">
            <span>© 2026 PORTNET S.A.</span>
            <nav className="auth-footer-links" aria-label="Liens légaux">
              {FOOTER_LINKS.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {link.label}
                </a>
              ))}
            </nav>
          </footer>
        </div>
      </div>

      <MFAPopup
        open={showMfaPopup}
        onClose={() => {
          setShowMfaPopup(false);
          setMfaErrorMessage("");
        }}
        qrCodeBase64={qrCodeUrl}
        secretCode={secret}
        codeOTP={codeOTP}
        setCodeOTP={setCodeOTP}
        handleRegisterClick={handleRegisterClick}
        mfaErrorMessage={mfaErrorMessage}
      />
    </div>
  );
};

export default Auth;
